import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UserKeys, deleteRole } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import { UserRole } from '../../../../types';
import { useState } from 'react';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { useNavigate, useParams } from 'react-router-dom';
import { pagesConfig } from '../../../shared/pages-config';
import { Messages } from '../../../utils/message-constants';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface DeleteRoleDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRole: UserRole;
  changeLogRoles: string[];
  roleNames: string[];
}

export default function DeleteRoleDialog({
  open,
  handleClose,
  selectedRole,
  changeLogRoles,
  roleNames,
}: DeleteRoleDialogProps) {
  const [transfer, setTransfer] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleError, setNewRoleError] = useState(false);
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { roleName } = useParams() as { roleName: string };
  const navigate = useNavigate();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      deleteRole(
        selectedRole.roleName,
        newRoleName,
        transfer,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Deleted('Role'),
      });

      queryClient.setQueryData<UserRole[]>([UserKeys.Roles], (oldData) => {
        if (!oldData) return;
        return oldData.filter(
          (role) => role.roleName !== selectedRole.roleName
        );
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.gPanelRoles],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.gPanelRole],
      });
      setChangeLogMessage('');
      setTransfer(false);
      if (roleName) {
        navigate(`/${pagesConfig.roleManager.link}`, {
          replace: true,
        });
      }
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Deleting('the selected role'),
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.role_manager.delete_role, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.roleManager,
        roleType: selectedRole.roleType,
      });
    },
  });

  const filteredRoleNames = roleNames.filter(
    (name) => name !== selectedRole.roleName
  );

  function handleSubmit() {
    if (transfer && newRoleName === '') {
      setNewRoleError(true);
    } else {
      setNewRoleError(false);
      if (
        isAbleToSeeUIElement(
          changeLogRoles,
          AdministrationRoles.DeleteRole.value
        )
      ) {
        setOpenChangeLogDialog(true);
      } else {
        mutate();
      }
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        setTransfer(false);
        setNewRoleName('');
        handleClose();
      }}
      title="Delete Role"
      dialogContent={
        <Box>
          <Typography>
            Delete <strong>{selectedRole.roleName}</strong>?
          </Typography>
          <RadioGroup
            value={transfer}
            onChange={() => {
              setTransfer(!transfer);
              if (!transfer) {
                setNewRoleName('');
              }
              if (newRoleError) {
                setNewRoleError(false);
              }
            }}
          >
            <FormControlLabel
              value={false}
              control={<Radio />}
              label="Remove all access for users of this Role."
            />
            <FormControlLabel
              value={true}
              control={<Radio />}
              label="Transfer users to a new Role."
            />
          </RadioGroup>
          <Autocomplete
            id="roles"
            options={filteredRoleNames}
            value={newRoleName}
            disabled={!transfer}
            autoHighlight
            onChange={(_event, value) => {
              if (!value) {
                setNewRoleName('');
              } else {
                setNewRoleName(value);
              }
            }}
            sx={{ width: 400, pb: 3 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select a role"
                error={newRoleError}
                helperText={
                  newRoleError
                    ? 'Please provide a role to transfer the users too.'
                    : ' '
                }
              />
            )}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Delete Role',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'error',
      }}
    />
  );
}
